import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Folder, FileText, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { Drawer, EmptyState, SkeletonLoader, useToast } from "@/components/system";
import { formatError } from "@/lib/format-error";
import { formatShortDate } from "@/lib/date-format";

/**
 * /evidence (SPEC 3.7) — v1 shell.
 *  - Library: list of seeded Evidence rows, grouped by domain. Tap → drawer
 *    with full claim + paper info.
 *  - Inbox: list of EvidenceInbox configs + "Add via folder watch" CTA which
 *    asks the browser File System Access API for a directory and writes the
 *    config row. Actual PDF scan + LLM extraction lands in BW9.
 */

type EvRow = {
  id: string;
  paper_id: string;
  claim: string;
  domains: string[];
  topics: string[];
  grade: string | null;
  paper:
    | {
        id: string;
        handle: string;
        title: string;
        year: number | null;
        journal: string | null;
        doi: string | null;
      }
    | { id: string; handle: string; title: string; year: number | null; journal: string | null; doi: string | null }[]
    | null;
};

type InboxRow = {
  id: string;
  name: string;
  path: string;
  path_kind: string;
  reference_manager_kind: string | null;
  active: boolean;
  last_scanned_at: string | null;
  pdfs_processed_count: number;
};

export function Evidence() {
  return (
    <article aria-labelledby="evidence-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Evidence
      </p>
      <h1
        id="evidence-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        What we know.
      </h1>
      <div style={{ display: "grid", gap: "var(--space-6)" }}>
        <UploadSection />
        <InboxSection />
        <LibrarySection />
      </div>
    </article>
  );
}

function UploadSection() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);

  const WORKER_URL =
    (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
    "https://haus-worker.rackljo.workers.dev";

  const uploads = useQuery({
    queryKey: ["evidence_inbox_uploads", userId],
    enabled: Boolean(userId),
    staleTime: 10_000,
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await (supabase as unknown as {
        from: (t: string) => ReturnType<typeof supabase.from>;
      })
        .from("evidence_inbox_upload")
        .select("id, filename, status, error_message, paper_id, created_at, processed_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });

  const upload = useMutation({
    mutationFn: async () => {
      if (!file || !userId) throw new Error("Pick a PDF");
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.access_token) throw new Error("Not signed in");

      // For now we skip actual R2 upload — the worker accepts a storage_url
      // pointing to an externally hosted file. As an interim, we use a data:
      // URL so the worker can fetch the bytes back on next process tick.
      const arr = new Uint8Array(await file.arrayBuffer());
      let bin = "";
      for (let i = 0; i < arr.length; i += 0x8000) bin += String.fromCharCode(...arr.subarray(i, i + 0x8000));
      const dataUrl = `data:application/pdf;base64,${btoa(bin)}`;

      const resp = await fetch(`${WORKER_URL}/api/evidence/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${s.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          filename: file.name,
          size_bytes: file.size,
          mime_type: file.type || "application/pdf",
          storage_url: dataUrl,
        }),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status}: ${t.slice(0, 200)}`);
      }

      // Trigger immediate processing.
      await fetch(`${WORKER_URL}/api/evidence/process`, {
        method: "POST",
        headers: { Authorization: `Bearer ${s.access_token}` },
      });
    },
    onSuccess: () => {
      toast.show("PDF queued for extraction.");
      setFile(null);
      void qc.invalidateQueries({ queryKey: ["evidence_inbox_uploads"] });
      void qc.invalidateQueries({ queryKey: ["evidence_library"] });
    },
    onError: (e) => toast.show(formatError(e)),
  });

  return (
    <section
      style={{
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "var(--space-3)",
          gap: "var(--space-3)",
          flexWrap: "wrap",
        }}
      >
        <h3 style={{ fontSize: "var(--type-body)", fontWeight: 500, margin: 0 }}>
          Upload a paper
        </h3>
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
          Anthropic extracts title + claims · ~$0.05 per PDF
        </p>
      </header>
      <div
        style={{
          display: "flex",
          gap: "var(--space-3)",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 12px",
            background: "var(--bg-3)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            color: "var(--fg)",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
          }}
        >
          <FileText size={14} strokeWidth={1.5} />
          {file ? file.name : "Choose PDF"}
          <input
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <button
          type="button"
          onClick={() => upload.mutate()}
          disabled={!file || upload.isPending}
          style={{
            padding: "8px 14px",
            background: file ? "var(--accent)" : "var(--bg-3)",
            color: file ? "#0a0b0d" : "var(--fg-3)",
            border: "none",
            borderRadius: "var(--radius-card)",
            fontSize: "var(--type-body-s)",
            fontWeight: 500,
            cursor: upload.isPending ? "wait" : "pointer",
          }}
        >
          {upload.isPending ? "Uploading…" : "Upload + extract"}
        </button>
      </div>
      {(uploads.data ?? []).length > 0 && (
        <ul style={{ margin: "var(--space-3) 0 0", padding: 0, listStyle: "none", display: "grid", gap: 4 }}>
          {(uploads.data ?? []).map((u: unknown) => {
            const row = u as {
              id: string;
              filename: string;
              status: string;
              error_message: string | null;
              paper_id: string | null;
              created_at: string;
            };
            return (
              <li
                key={row.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: "var(--space-2)",
                  alignItems: "baseline",
                  padding: "6px 0",
                  borderBottom: "1px solid var(--hair)",
                  fontSize: "var(--type-body-s)",
                }}
              >
                <span style={{ color: "var(--fg)" }}>{row.filename}</span>
                <span
                  className="mono"
                  style={{
                    color:
                      row.status === "extracted"
                        ? "var(--accent)"
                        : row.status === "failed"
                          ? "var(--brick)"
                          : "var(--fg-3)",
                    fontSize: "var(--type-meta)",
                  }}
                >
                  {row.status}
                </span>
                <span className="mono" style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
                  {formatShortDate(row.created_at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function LibrarySection() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const [open, setOpen] = useState<EvRow | null>(null);

  const q = useQuery<EvRow[]>({
    queryKey: ["evidence_library", userId],
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("evidence")
        .select(
          "id, paper_id, claim, domains, topics, grade, paper:paper_id (id, handle, title, year, journal, doi)",
        )
        .eq("user_id", userId)
        .order("paper_id");
      if (error) throw error;
      return ((data ?? []) as unknown) as EvRow[];
    },
  });

  const grouped = useMemo(() => {
    const map = new Map<string, EvRow[]>();
    for (const e of q.data ?? []) {
      const dom = e.domains[0] ?? "general";
      const list = map.get(dom) ?? [];
      list.push(e);
      map.set(dom, list);
    }
    return map;
  }, [q.data]);

  return (
    <section>
      <h3
        style={{
          fontSize: "var(--type-body)",
          fontWeight: 500,
          margin: "0 0 var(--space-3)",
        }}
      >
        Library
      </h3>
      {q.isLoading ? (
        <SkeletonLoader variant="card" count={3} height={64} />
      ) : (q.data ?? []).length === 0 ? (
        <EmptyState
          headline="No evidence yet."
          sub="Run the seeds from Settings → Data to bootstrap 14 papers + claims."
        />
      ) : (
        <div style={{ display: "grid", gap: "var(--space-4)" }}>
          {[...grouped.entries()].map(([domain, rows]) => (
            <div key={domain}>
              <p
                style={{
                  fontSize: "var(--type-meta)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--fg-3)",
                  margin: "0 0 6px",
                }}
              >
                {domain}
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 6 }}>
                {rows.map((e) => {
                  const paper = Array.isArray(e.paper) ? e.paper[0] : e.paper;
                  return (
                    <li key={e.id}>
                      <button
                        type="button"
                        onClick={() => setOpen(e)}
                        style={{
                          width: "100%",
                          display: "grid",
                          gridTemplateColumns: "1fr auto",
                          gap: "var(--space-3)",
                          padding: "var(--space-3)",
                          background: "var(--bg-2)",
                          border: "1px solid var(--hair)",
                          borderRadius: "var(--radius-chip)",
                          color: "var(--fg)",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                      >
                        <span>
                          <span style={{ color: "var(--fg)", fontSize: "var(--type-body-s)" }}>
                            {e.claim}
                          </span>
                          {paper && (
                            <span
                              style={{
                                display: "block",
                                color: "var(--fg-3)",
                                fontSize: "var(--type-meta)",
                                marginTop: 4,
                              }}
                            >
                              {paper.handle}
                              {paper.journal ? ` · ${paper.journal}` : ""}
                              {paper.year ? ` · ${paper.year}` : ""}
                            </span>
                          )}
                        </span>
                        {e.grade && (
                          <span
                            className="mono"
                            style={{
                              color: e.grade === "A" ? "var(--accent)" : "var(--fg-3)",
                              fontSize: "var(--type-meta)",
                            }}
                          >
                            {e.grade}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Drawer open={open != null} onClose={() => setOpen(null)} title={open ? "Evidence" : ""}>
        {open && <EvidenceDetail evidence={open} />}
      </Drawer>
    </section>
  );
}

function EvidenceDetail({ evidence }: { evidence: EvRow }) {
  const paper = Array.isArray(evidence.paper) ? evidence.paper[0] : evidence.paper;
  return (
    <div style={{ display: "grid", gap: "var(--space-4)" }}>
      <p style={{ margin: 0, fontSize: "var(--type-body)", lineHeight: 1.55 }}>
        {evidence.claim}
      </p>
      {paper && (
        <div
          style={{
            borderLeft: "2px solid var(--accent)",
            paddingLeft: "var(--space-3)",
            color: "var(--fg-2)",
          }}
        >
          <p style={{ margin: 0, fontSize: "var(--type-body-s)", fontWeight: 500 }}>
            {paper.title}
          </p>
          <p style={{ margin: "4px 0 0", fontSize: "var(--type-meta)" }}>
            {paper.handle}
            {paper.journal ? ` · ${paper.journal}` : ""}
            {paper.year ? ` · ${paper.year}` : ""}
            {paper.doi && (
              <>
                {" · "}
                <a
                  href={`https://doi.org/${paper.doi}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ color: "var(--accent)" }}
                >
                  doi
                </a>
              </>
            )}
          </p>
        </div>
      )}
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 4, fontSize: "var(--type-meta)", color: "var(--fg-3)" }}
      >
        {evidence.topics.map((t) => (
          <span
            key={t}
            style={{
              padding: "2px 8px",
              border: "1px solid var(--hair)",
              borderRadius: 999,
            }}
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function InboxSection() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);

  const q = useQuery<InboxRow[]>({
    queryKey: ["evidence_inbox", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("evidence_inbox")
        .select(
          "id, name, path, path_kind, reference_manager_kind, active, last_scanned_at, pdfs_processed_count",
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const create = useMutation({
    mutationFn: async (folder: { path: string; rmKind: string | null }) => {
      if (!userId) throw new Error("Not signed in");
      const { error } = await supabase.from("evidence_inbox").insert({
        user_id: userId,
        name: folder.path.split("/").pop() || folder.path,
        path: folder.path,
        path_kind: folder.rmKind ? "reference_manager" : "local",
        reference_manager_kind: folder.rmKind,
        import_format: "pdf_only",
        watch_strategy: "manual_trigger",
        file_pattern: "*.pdf",
        extraction_strategy: "llm_full_text",
        after_extraction_action: "leave_in_place",
        active: true,
        notes: "Folder watch set via browser File System Access API. Scan + extraction pending BW9.",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["evidence_inbox"] });
      setAdding(false);
      toast.show("Folder watch added.");
    },
    onError: (err) => toast.show(`Could not add — ${formatError(err)}`),
  });

  return (
    <section>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "var(--space-3)",
        }}
      >
        <h3 style={{ fontSize: "var(--type-body)", fontWeight: 500, margin: 0 }}>
          EvidenceInbox
        </h3>
        <button
          type="button"
          onClick={() => setAdding(true)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "6px 12px",
            background: "transparent",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-chip)",
            color: "var(--fg)",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
          }}
        >
          <Plus size={14} strokeWidth={1.6} /> Add folder
        </button>
      </header>

      {q.isLoading ? (
        <SkeletonLoader variant="card" count={1} height={56} />
      ) : (q.data ?? []).length === 0 ? (
        <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          No watch folders yet. Add one to point at your reference-manager export.
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 6 }}>
          {(q.data ?? []).map((row) => (
            <li
              key={row.id}
              style={{
                display: "grid",
                gridTemplateColumns: "20px 1fr auto",
                gap: "var(--space-3)",
                alignItems: "center",
                padding: "var(--space-3)",
                background: "var(--bg-2)",
                border: "1px solid var(--hair)",
                borderRadius: "var(--radius-chip)",
                fontSize: "var(--type-body-s)",
              }}
            >
              {row.reference_manager_kind ? (
                <FileText size={16} strokeWidth={1.6} aria-hidden />
              ) : (
                <Folder size={16} strokeWidth={1.6} aria-hidden />
              )}
              <span>
                <span style={{ color: "var(--fg)" }}>{row.name}</span>
                <span style={{ color: "var(--fg-3)", marginLeft: 6, fontSize: "var(--type-meta)" }}>
                  {row.path}
                </span>
              </span>
              <span className="mono" style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
                {row.last_scanned_at ? `scanned ${formatShortDate(row.last_scanned_at)}` : "never"}
                {" · "}
                {row.pdfs_processed_count} pdfs
              </span>
            </li>
          ))}
        </ul>
      )}

      {adding && <FolderPickerModal onClose={() => setAdding(false)} onSubmit={(payload) => create.mutate(payload)} pending={create.isPending} />}
    </section>
  );
}

function FolderPickerModal({
  onClose,
  onSubmit,
  pending,
}: {
  onClose: () => void;
  onSubmit: (payload: { path: string; rmKind: string | null }) => void;
  pending: boolean;
}) {
  const [path, setPath] = useState("");
  const [rmKind, setRmKind] = useState<string>("");

  async function pickDir() {
    type WindowWithFS = Window & {
      showDirectoryPicker?: () => Promise<{ name: string }>;
    };
    const w = window as WindowWithFS;
    if (typeof w.showDirectoryPicker !== "function") {
      window.alert(
        "Your browser doesn't expose the File System Access API. Type the path manually instead.",
      );
      return;
    }
    try {
      const handle = await w.showDirectoryPicker();
      // We can't get the absolute path from the API; record the chosen folder
      // name as a hint and the user can edit it.
      setPath(`~/${handle.name}/`);
    } catch (err) {
      // user cancelled
      void err;
    }
  }

  return (
    <Drawer open onClose={onClose} title="Add evidence folder">
      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}>
          Point Haus at your reference-manager library or any PDF folder. v1
          stores the config; LLM extraction lands in BW9.
        </p>

        <button
          type="button"
          onClick={pickDir}
          style={{
            padding: "8px 14px",
            background: "var(--accent)",
            color: "#0a0b0d",
            border: "none",
            borderRadius: "var(--radius-card)",
            fontSize: "var(--type-body-s)",
            fontWeight: 500,
            cursor: "pointer",
            justifySelf: "flex-start",
          }}
        >
          Pick a folder…
        </button>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>Folder path</span>
          <input
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="~/Documents/References/EndNote-Library/"
            style={{
              padding: "8px 10px",
              background: "var(--bg-3)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-chip)",
              color: "var(--fg)",
              fontSize: "var(--type-body-s)",
              fontFamily: "var(--font-mono)",
            }}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>
            Reference manager (optional)
          </span>
          <select
            value={rmKind}
            onChange={(e) => setRmKind(e.target.value)}
            style={{
              padding: "8px 10px",
              background: "var(--bg-3)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-chip)",
              color: "var(--fg)",
              fontSize: "var(--type-body-s)",
            }}
          >
            <option value="">Plain folder</option>
            <option value="endnote">EndNote</option>
            <option value="zotero">Zotero</option>
            <option value="mendeley">Mendeley</option>
            <option value="paperpile">Paperpile</option>
            <option value="readcube_papers">ReadCube Papers</option>
          </select>
        </label>

        <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 14px",
              background: "transparent",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-card)",
              color: "var(--fg-2)",
              fontSize: "var(--type-body-s)",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!path || pending}
            onClick={() => onSubmit({ path, rmKind: rmKind || null })}
            style={{
              padding: "8px 14px",
              background: path ? "var(--accent)" : "var(--bg-3)",
              color: path ? "#0a0b0d" : "var(--fg-3)",
              border: "none",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
              fontWeight: 500,
              cursor: pending ? "not-allowed" : "pointer",
            }}
          >
            {pending ? "Adding…" : "Add folder"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}
